import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Button, Card, Row, Col, Typography, Space, Divider, Alert } from 'antd'
import MaterialIcon from '../components/MaterialIcon'
import { getWebPortalUrl } from '../config/runtimeEnv'
import { useSSRSearch, useSSRWebPortalUrl, useSSRBaseUrl } from '../ssr/SSRContext'

const { Title, Text, Paragraph } = Typography

const LANDING_TITLE = 'Gmail for Business | ServiceFlow Pro – Email Efficiency for Service Companies'
const LANDING_DESCRIPTION = 'Unified Gmail inbox with AI responses, customer linking, and estimates for HVAC, plumbing, electrical, and other service-based industries. Part of ServiceFlow Pro.'

const USAGE_IMAGES = [
  { path: 'assets/screenshot-inbox.png', alt: 'Unified inbox view', caption: 'Unified inbox' },
  { path: 'assets/screenshot-thread.png', alt: 'Email thread with context', caption: 'Thread & context' },
  { path: 'assets/screenshot-ai.png', alt: 'AI and customer linking', caption: 'AI & linking' },
  { path: 'assets/screenshot-efficiency.png', alt: 'Efficiency tools', caption: 'Efficiency tools' }
]

const LandingPage: React.FC = () => {
  const ssrWebPortalUrl = useSSRWebPortalUrl()
  const webPortalUrl = ssrWebPortalUrl || getWebPortalUrl()
  const baseUrl = useSSRBaseUrl() || (typeof window !== 'undefined' ? window.location.origin : 'https://gmail.serviceflow-pro.com')
  const searchString = useSSRSearch() || (typeof window !== 'undefined' ? window.location.search : '')
  const sessionExpired = new URLSearchParams(searchString).get('session_expired') === '1'
  const productUrl = `${webPortalUrl}/product`

  const softwareApplicationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Gmail for Business',
    description: LANDING_DESCRIPTION,
    url: baseUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    featureList: [
      'Unified inbox',
      'AI-powered email responses',
      'Bulk email campaigns',
      'Auto customer and job linking',
      'Estimates and jobs from email',
      'Price book integration',
      'Email analytics and tracking'
    ],
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Part of ServiceFlow Pro – free trial available'
    },
    provider: {
      '@type': 'Organization',
      name: 'ServiceFlow Pro',
      url: webPortalUrl
    }
  }

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ServiceFlow Pro',
    url: webPortalUrl,
    description: 'Field service management and business email for service-based industries.'
  }

  const webSiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Gmail for Business',
    url: baseUrl,
    description: LANDING_DESCRIPTION,
    publisher: { '@type': 'Organization', name: 'ServiceFlow Pro', url: webPortalUrl }
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl.replace(/\/$/, '') }]
  }

  const handleGetStarted = () => {
    window.location.href = `${webPortalUrl}/login`
  }

  const features = [
    {
      icon: 'inbox',
      color: '#1890ff',
      title: 'Unified Inbox',
      description: 'Access all your business emails in one beautiful, organized interface. No more switching between tabs.'
    },
    {
      icon: 'psychology',
      color: '#52c41a',
      title: 'AI-Powered Responses',
      description: 'Generate intelligent email responses instantly. Save hours with AI that understands your business context.'
    },
    {
      icon: 'groups',
      color: '#722ed1',
      title: 'Bulk Email Campaigns',
      description: 'Send personalized emails to multiple customers at once. Track opens and clicks in real-time.'
    },
    {
      icon: 'link',
      color: '#fa8c16',
      title: 'Auto Customer Linking',
      description: 'Automatically connect emails to customers, jobs, and estimates. Complete context at your fingertips.'
    },
    {
      icon: 'description',
      color: '#13c2c2',
      title: 'Smart Estimates & Jobs',
      description: 'Create estimates and jobs directly from emails with AI extraction. Turn conversations into revenue.'
    },
    {
      icon: 'analytics',
      color: '#eb2f96',
      title: 'Email Analytics',
      description: 'Track open rates, click rates, and engagement. Make data-driven decisions for your business.'
    },
    {
      icon: 'shopping_cart',
      color: '#faad14',
      title: 'Price Book Integration',
      description: 'Insert items from your price book directly into emails. Build quotes without leaving your inbox.'
    },
    {
      icon: 'visibility_off',
      color: '#595959',
      title: 'Privacy Controls',
      description: 'Toggle tracking on/off per email. Full control over what data you collect.'
    }
  ]

  const benefits = [
    {
      stat: '70%',
      label: 'Time Saved',
      description: 'Reduce email management time with AI and automation'
    },
    {
      stat: '3x',
      label: 'Faster Responses',
      description: 'Reply to customers instantly with AI-generated responses'
    },
    {
      stat: '95%',
      label: 'Open Rate',
      description: 'Track email performance and optimize your communication'
    },
    {
      stat: '∞',
      label: 'Scalability',
      description: 'Send personalized emails to unlimited customers at once'
    }
  ]

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Helmet>
        <title>{LANDING_TITLE}</title>
        <meta name="description" content={LANDING_DESCRIPTION} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={baseUrl} />
        <meta property="og:title" content={LANDING_TITLE} />
        <meta property="og:description" content={LANDING_DESCRIPTION} />
        <meta property="og:url" content={baseUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(softwareApplicationJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(webSiteJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      {sessionExpired && (
        <Alert
          type="warning"
          showIcon
          message="Session expired or invalid link"
          description="Please log in at the main portal, then open Gmail again from Integrations (or use the link with a fresh token)."
          style={{ margin: 16, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
          action={
            <Button size="small" type="primary" onClick={handleGetStarted}>
              Log in
            </Button>
          }
        />
      )}
      {/* Top nav */}
      <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 24 }}>
        <a href="/faq" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', fontWeight: 500 }}>FAQ</a>
        <a href={productUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', fontWeight: 500 }}>ServiceFlow Pro</a>
      </div>
      {/* Hero Section */}
      <div style={{ 
        padding: '80px 24px 60px',
        textAlign: 'center',
        color: 'white'
      }}>
        <MaterialIcon 
          icon="mail" 
          style={{ 
            fontSize: '80px',
            marginBottom: '24px',
            display: 'block',
            filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2))'
          }} 
        />
        
        <Title level={1} style={{ 
          color: 'white',
          fontSize: '48px',
          fontWeight: 'bold',
          marginBottom: '16px',
          textShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          Gmail for Business
        </Title>
        
        <Title level={3} style={{ 
          color: 'rgba(255,255,255,0.9)',
          fontWeight: 'normal',
          marginBottom: '32px',
          maxWidth: '700px',
          margin: '0 auto 32px'
        }}>
          Transform your inbox into a powerful business tool with AI-driven automation, customer insights, and advanced analytics
        </Title>

        <Space size="large">
          <Button 
            type="primary" 
            size="large"
            icon={<MaterialIcon icon="login" />}
            onClick={handleGetStarted}
            style={{
              height: '50px',
              padding: '0 40px',
              fontSize: '16px',
              background: 'white',
              color: '#667eea',
              border: 'none',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            Get Started
          </Button>
          <Button 
            size="large"
            icon={<MaterialIcon icon="play_circle" />}
            style={{
              height: '50px',
              padding: '0 40px',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '2px solid white',
              fontWeight: '600',
              backdropFilter: 'blur(10px)'
            }}
            href="#features"
          >
            Learn More
          </Button>
        </Space>
      </div>

      {/* Stats Section */}
      <div style={{ 
        background: 'white',
        padding: '60px 24px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Row gutter={[32, 32]}>
            {benefits.map((benefit, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={1} style={{ 
                    color: '#667eea',
                    fontSize: '56px',
                    fontWeight: 'bold',
                    marginBottom: '8px'
                  }}>
                    {benefit.stat}
                  </Title>
                  <Title level={4} style={{ marginBottom: '8px' }}>
                    {benefit.label}
                  </Title>
                  <Text type="secondary">
                    {benefit.description}
                  </Text>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" style={{ 
        padding: '80px 24px',
        background: '#f5f5f5'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <Title level={2} style={{ fontSize: '40px', marginBottom: '16px' }}>
              Everything You Need to Scale
            </Title>
            <Paragraph style={{ fontSize: '18px', color: '#666' }}>
              Powerful features that transform how you manage business communication
            </Paragraph>
          </div>

          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} md={8} key={index}>
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '32px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '12px',
                    background: `${feature.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <MaterialIcon 
                      icon={feature.icon} 
                      style={{ fontSize: '32px', color: feature.color }} 
                    />
                  </div>
                  <Title level={4} style={{ marginBottom: '12px' }}>
                    {feature.title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '15px', lineHeight: '1.6' }}>
                    {feature.description}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* How It Works Section */}
      <div style={{ 
        padding: '80px 24px',
        background: 'white'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <Title level={2} style={{ fontSize: '40px', marginBottom: '16px' }}>
              How It Makes Your Business More Efficient
            </Title>
            <Paragraph style={{ fontSize: '18px', color: '#666' }}>
              See how Gmail for Business streamlines your workflow
            </Paragraph>
          </div>

          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} md={12}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px',
                padding: '40px',
                color: 'white',
                height: '100%'
              }}>
                <Title level={3} style={{ color: 'white', marginBottom: '24px' }}>
                  <MaterialIcon icon="schedule" style={{ marginRight: '12px', verticalAlign: 'middle' }} />
                  Save Hours Every Week
                </Title>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5} style={{ color: 'white', marginBottom: '8px' }}>
                      ⚡ Instant AI Responses
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                      Generate professional responses in seconds with context-aware AI
                    </Text>
                  </div>
                  <div>
                    <Title level={5} style={{ color: 'white', marginBottom: '8px' }}>
                      🎯 Smart Automation
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                      Auto-link emails to customers, jobs, and estimates automatically
                    </Text>
                  </div>
                  <div>
                    <Title level={5} style={{ color: 'white', marginBottom: '8px' }}>
                      📊 Bulk Operations
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                      Send personalized emails to hundreds of customers at once
                    </Text>
                  </div>
                </Space>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <div style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                borderRadius: '16px',
                padding: '40px',
                color: 'white',
                height: '100%'
              }}>
                <Title level={3} style={{ color: 'white', marginBottom: '24px' }}>
                  <MaterialIcon icon="trending_up" style={{ marginRight: '12px', verticalAlign: 'middle' }} />
                  Increase Revenue
                </Title>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5} style={{ color: 'white', marginBottom: '8px' }}>
                      💰 Quick Quotes
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                      Insert price book items directly into emails and close deals faster
                    </Text>
                  </div>
                  <div>
                    <Title level={5} style={{ color: 'white', marginBottom: '8px' }}>
                      📈 Track Performance
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                      Monitor open rates and click rates to optimize your outreach
                    </Text>
                  </div>
                  <div>
                    <Title level={5} style={{ color: 'white', marginBottom: '8px' }}>
                      🚀 Faster Conversions
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
                      Create estimates and jobs from emails with one click
                    </Text>
                  </div>
                </Space>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Usage / Screenshots Section */}
      <div style={{ padding: '80px 24px', background: '#f5f5f5' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <Title level={2} style={{ fontSize: '40px', marginBottom: '16px' }}>
              See It In Action
            </Title>
            <Paragraph style={{ fontSize: '18px', color: '#666' }}>
              How service teams use Gmail for Business every day
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {USAGE_IMAGES.map((img, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <a
                    href={`${baseUrl}/${img.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      height: 260,
                      overflow: 'hidden',
                      flexShrink: 0,
                      cursor: 'pointer',
                      textDecoration: 'none'
                    }}
                  >
                    <img
                      src={`${baseUrl}/${img.path}`}
                      alt={img.alt}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  </a>
                  <div style={{ padding: '12px 16px', textAlign: 'center', flexShrink: 0 }}>
                    <Text type="secondary">{img.caption}</Text>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{ 
        padding: '80px 24px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Title level={2} style={{ 
            color: 'white',
            fontSize: '36px',
            marginBottom: '24px'
          }}>
            Ready to Transform Your Business Email?
          </Title>
          <Paragraph style={{ 
            fontSize: '18px',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '40px'
          }}>
            Join hundreds of businesses using Gmail for Business to save time, increase efficiency, and grow revenue.
          </Paragraph>
          <Button 
            type="primary"
            size="large"
            icon={<MaterialIcon icon="rocket_launch" />}
            onClick={handleGetStarted}
            style={{
              height: '56px',
              padding: '0 48px',
              fontSize: '18px',
              background: 'white',
              color: '#667eea',
              border: 'none',
              fontWeight: '600',
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
            }}
          >
            Start Using Gmail for Business
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div style={{ 
        padding: '32px 24px',
        background: '#1f1f1f',
        textAlign: 'center',
        color: 'rgba(255,255,255,0.65)'
      }}>
        <Space split={<Divider type="vertical" style={{ borderColor: 'rgba(255,255,255,0.2)' }} />} wrap>
          <a href="/" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}>Home</a>
          <a href="/faq" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}>FAQ</a>
          <a href={productUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}>
            ServiceFlow Pro Product
          </a>
          <Text style={{ color: 'rgba(255,255,255,0.65)' }}>
            © {new Date().getFullYear()} ServiceFlow Pro
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.65)' }}>
            <MaterialIcon icon="security" style={{ marginRight: '4px', verticalAlign: 'middle', fontSize: '16px' }} />
            Enterprise Security
          </Text>
        </Space>
      </div>
    </div>
  )
}

export default LandingPage

